import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'styled-bootstrap-grid';

import { fromDate, createExportDeadline, updateExportDeadline, toDate } from '@/services/ws-invoice-sync/src/main/resources/rest-api';
import type { Dayjs } from '@/js/libs/dayjs';
import { useForm, DatePickerField } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button, FormGroup, Modal } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { useSuccessNotification } from 'src/hooks/useSuccessNotification';
import { createSchema } from './schema';
import type { FormValues, BookingPeriodFormPopupProps } from './types';

export const BookingPeriodFormPopup: React.FC<BookingPeriodFormPopupProps> = React.memo(function BookingPeriodFormPopup({ state }) {
  const {
    payload: { item, callback },
  } = state;
  const [t] = useTranslation(['backend', 'common', 'messages', 'finances']);

  const createBookingPeriodRequest = useLazyRequest({
    apiMethod: createExportDeadline,
    onSuccess: useSuccessNotification(t('messages:BOOKING_PERIOD_SUCCESSFULLY_CREATED')),
  });

  const updateBookingPeriodRequest = useLazyRequest({
    apiMethod: updateExportDeadline,
    onSuccess: useSuccessNotification(t('messages:BOOKING_PERIOD_SUCCESSFULLY_UPDATED')),
  });

  const form = useForm<FormValues>({
    defaultValues: item ? { month: fromDate(item.month), deadline: fromDate(item.deadline) } : { month: null, deadline: null },
    resolver: zodResolver(createSchema(t)),
    mode: 'onChange',
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
  } = form;

  const handleMonthChange = useCallbackRef((value: Dayjs | null) => {
    if (value) {
      const firstDayOfMonth = value.startOf('month');
      form.setValue('month', firstDayOfMonth);
    } else {
      form.setValue('month', null);
    }
  });

  const onSubmit = useCallbackRef(async (values: FormValues) => {
    if (!values.month || !values.deadline) {
      throw new Error('Required fields are empty');
    }

    const exportDeadlineForm = { month: toDate(values.month), deadline: toDate(values.deadline) };
    const promise = item?.id
      ? updateBookingPeriodRequest.load({ exportDeadlineForm, id: item.id })
      : createBookingPeriodRequest.load({ exportDeadlineForm });

    const { data } = await promise;
    callback(data);

    state.close();
  });

  return (
    <Modal state={state} title={item ? t('finances:UPDATE_BOOKING_PERIOD') : t('finances:CREATE_BOOKING_PERIOD')}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body data-testid='company-name-form'>
            <FormGroup disabledSpacer>
              <Col md={6}>
                <DatePickerField
                  control={control}
                  name='month'
                  label={t('common:MONTH')}
                  isClearable
                  required
                  onChange={handleMonthChange}
                  disabled={!!item || isSubmitting}
                />
              </Col>
              <Col md={6}>
                <DatePickerField
                  control={control}
                  name='deadline'
                  label={t('common:DEADLINE')}
                  isClearable
                  required
                  disabled={isSubmitting}
                />
              </Col>
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button ghost onClick={state.close} disabled={isSubmitting} dataTestId='cancel-button'>
              {t('common:CLOSE')}
            </Button>
            <Button type='submit' primary isLoading={isSubmitting} disabled={isSubmitting} dataTestId='submit-button'>
              {t('common:SUBMIT')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});
